import { createRootRoute } from "@tanstack/react-router";

import { RootDocument, GlobalErrorBoundary } from "@/components/RootDocument";
import { initSentry } from "@/lib/sentry";

import indexCss from "../App.css?url";

// Initialize Sentry immediately when module loads
initSentry();

const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Flowstep",
  url: "https://flowstep.ai",
  logo: "https://flowstep.ai/logo512.png",
  sameAs: [
    "https://x.com/flowstep_ai",
    "https://www.linkedin.com/company/flowstep-ai/",
    "https://www.youtube.com/@flowstep_ai",
  ],
};

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Flowstep",
  url: "https://flowstep.ai",
};

const homepageTitle = "Flowstep: Your AI Design Tool";
export const homepageDescription =
  "Flowstep is an AI design tool that generates multi-screen UI from prompts. Edit via chat or manually, copy to Figma, export code, or connect your IDE via MCP.";

export const Route = createRootRoute({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: homepageTitle,
      },
      {
        name: "description",
        content: homepageDescription,
      },
      { property: "og:title", content: homepageTitle },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Flowstep" },
      { property: "og:locale", content: "en_US" },
      {
        property: "og:description",
        content: homepageDescription,
      },
      { property: "og:image", content: "https://flowstep.ai/og-image.png" },
      {
        property: "og:image:secure_url",
        content: "https://flowstep.ai/og-image.png",
      },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "2400" },
      { property: "og:image:height", content: "1256" },
      {
        property: "og:image:alt",
        content: "Flowstep — generate editable UI and wireframes with AI",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@flowstep_ai" },
      { name: "twitter:creator", content: "@flowstep_ai" },
      {
        name: "twitter:title",
        content: homepageTitle,
      },
      {
        name: "twitter:description",
        content: homepageDescription,
      },
      { name: "twitter:image", content: "https://flowstep.ai/og-image.png" },
      {
        name: "twitter:image:alt",
        content: "Flowstep — generate editable UI and wireframes with AI",
      },
      { "script:ld+json": organizationJsonLd },
      { "script:ld+json": websiteJsonLd },
      { name: "apple-mobile-web-app-title", content: "Flowstep" },
      { name: "application-name", content: "Flowstep" },
      { name: "msapplication-TileColor", content: "#0247fe" },
      { name: "theme-color", content: "#ffffff" },
      {
        name: "google-site-verification",
        content: "mEJT5sJTRA99acC45Dt6NZ_UerhZq_Tq_1PgyJDRWZI",
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: indexCss,
      },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
      },

      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png?v=2.1",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-32x32.png?v=2.1",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/favicon-16x16.png?v=2.1",
      },
      { rel: "manifest", href: "/site.webmanifest?v=2.1" },
      {
        rel: "mask-icon",
        href: "/safari-pinned-tab.svg?v=2.1",
        color: "#0247fe",
      },
      { rel: "shortcut icon", href: "/favicon.ico?v=2.1" },
    ],
    scripts: [
      {
        src: "https://plausible.io/js/pa-OFXb2G6xypeBFyQ5sPtTp.js",
        async: true,
      },
      {
        children:
          "window.plausible=window.plausible||function(){(plausible.q=plausible.q||[]).push(arguments)},plausible.init=plausible.init||function(i){plausible.o=i||{}};plausible.init()",
      },
    ],
  }),

  errorComponent: ({ error }: { error: unknown }) => (
    <GlobalErrorBoundary
      error={error instanceof Error ? error : new Error(String(error))}
    />
  ),
  shellComponent: RootDocument,
});
